import React, { useState, useEffect } from "react";
import { Modal, Form, InputNumber, Button, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { TenantUsage, EditTenantLimits } from "./TenantUsageMonitor.types";
import { updateTenantUsageLimits } from "../../../services/AdminPortalAPIs";
import StyledTextInput from "src/components/reusable/StyledTextInput";

interface EditTenantLimitsModalProps {
  visible: boolean;
  tenant: TenantUsage | null;
  onCancel: () => void;
  onSuccess: () => void;
  onRefresh?: () => void;
}

const EditTenantLimitsModal: React.FC<EditTenantLimitsModalProps> = ({
  visible,
  tenant,
  onCancel,
  onSuccess,
  onRefresh,
}) => {
  const [form] = Form.useForm();
  const [editableFields, setEditableFields] = useState<Record<string, boolean>>(
    {},
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tenant) {
      form.setFieldsValue({
        tenant_id: tenant.tenant_id,
        chatbot_query_limit: tenant.chatbot_query_limit,
        chatbot_token_limit: tenant.chatbot_token_limit,
        email_quota: tenant.email_quota,
        template_generation_limit: tenant.template_generation_limit,
        template_token_limit: tenant.template_token_limit,
        social_listening_analysis_limit: tenant.social_listening_analysis_limit,
        social_listening_token_limit: tenant.social_listening_token_limit,
      });
      setEditableFields({});
    }
  }, [tenant, form]);

  const toggleEditable = (field: string) => {
    setEditableFields(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSave = async () => {
    try {
      const values: EditTenantLimits = await form.validateFields();
      setLoading(true);
      await updateTenantUsageLimits(values);
      message.success("Tenant limits updated successfully!");
      if (onRefresh) {
        onRefresh();
      }
      onSuccess(); // refresh parent
      onCancel(); // close modal
    } catch (err) {
      message.error( "Failed to update tenant limits");
    } finally {
      setLoading(false);
    }
  };

  const renderEditableField = (name: string, label: string) => (
    <Form.Item
      name={name}
      label={label}
      className={`edit-tenant-form-item edit-tenant-form-item-${name}`}
    >
      <InputNumber
        min={0}
        disabled={!editableFields[name]}
        className="edit-tenant-input"
        style={{ width: "100%" }}
        addonAfter={
          <Button
            type="text"
            size="small"
            className="edit-tenant-edit-btn"
            icon={<EditOutlined />}
            onClick={() => toggleEditable(name)}
          />
        }
      />
    </Form.Item>
  );

  return (
    <Modal
      className="edit-tenant-modal"
      title={`Edit Limits - ${tenant?.tenant_name || ""}`}
      open={visible}
      onCancel={onCancel}
      onOk={handleSave}
      okText="Save"
      confirmLoading={loading}
      destroyOnClose
    >
      <Form form={form} layout="vertical" className="edit-tenant-form">
        <Form.Item name="tenant_id" hidden>
          <StyledTextInput />
        </Form.Item>

        {renderEditableField("chatbot_query_limit", "Chatbot Query Limit")}
        {renderEditableField("chatbot_token_limit", "Chatbot Token Limit")}
        {renderEditableField("email_quota", "Email Quota")}
        {renderEditableField(
          "template_generation_limit",
          "Template Generation Limit",
        )}
        {renderEditableField("template_token_limit", "Template Token Limit")}
        {renderEditableField(
          "social_listening_analysis_limit",
          "Social Listening Analysis Limit",
        )}
        {renderEditableField(
          "social_listening_token_limit",
          "Social Listening Token Limit",
        )}
      </Form>
    </Modal>
  );
};

export default EditTenantLimitsModal;
